import request from '../../library/request';
import store from '../../store';
import {wrapXHR} from '../../functions/wrapXHR';
import type {AgentResponse, AiAgentChatLang, AiAgentFormId, AiAgentIntent} from './aiAgent.type';

export type SendAiAgentMessageParams = {
    prompt: string;
    history: unknown[];
    sessionId: string;
    scope: 'account' | 'event';
    currentRoute: string;
    eid?: number | null;
    pid?: number | null;
    formId?: AiAgentFormId | null;
    eventType?: string | null;
    sessionIntent?: AiAgentIntent | null;
    chatLang?: AiAgentChatLang | null;
    currentFormValues?: Record<string, unknown> | null;
    feature?: string;
    /** data:image/...;base64,... for vision — current turn only */
    imageDataUrl?: string;
};

export const sendAiAgentMessage = ({
    prompt,
    history,
    sessionId,
    scope,
    currentRoute,
    eid,
    pid,
    formId = null,
    eventType = null,
    sessionIntent = null,
    chatLang = null,
    currentFormValues = null,
    feature = 'aiAgent',
    imageDataUrl,
}: SendAiAgentMessageParams): Promise<AgentResponse | null> =>
    wrapXHR('aiAgent.sendMessageXHR', async () => {
        const body = {
            prompt,
            history,
            sessionId,
            feature,
            scope,
            currentRoute,
            ...(formId ? {formId} : {}),
            ...(eventType ? {eventType} : {}),
            ...(sessionIntent ? {sessionIntent} : {}),
            ...(chatLang ? {chatLang} : {}),
            ...(currentFormValues && Object.keys(currentFormValues).length
                ? {currentFormValues}
                : {}),
            ...(imageDataUrl ? {imageDataUrl} : {}),
        };

        const res = await request(
            'aiAgent.sendMessage',
            'post',
            `${scope === 'event' && eid ? `/event/${eid}` : `/package/${pid}`}/aiAgentMessage`,
            body,
            {handleError: true},
        );

        const agentPayload = res?.agentPayload as AgentResponse | undefined;
        if(!agentPayload || typeof agentPayload.text !== 'string' || !agentPayload.uiAction?.type) {
            return null;
        }
        return agentPayload;
    });

export const getAiAgentContext = () => {
    const state = store.getState();
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
    const eventMatch = pathname.match(/\/e\/(\d+)/);
    const packageMatch = pathname.match(/\/p\/(\d+)/);
    // Only use eid from the URL — stale event.eid in Redux must not be sent on account pages
    // (permission middleware would check the wrong event and return 500).
    const eid = eventMatch ? Number(eventMatch[1]) : null;
    const pid = packageMatch
        ? Number(packageMatch[1])
        : (state.account?.pid ?? state.event?.pid ?? null);
    const scope: 'account' | 'event' = eid ? 'event' : 'account';
    const eventType = state.aiAgent?.sessionEventType
        ?? (eid ? state.event?.type : null)
        ?? null;
    return {
        eid,
        pid,
        scope,
        currentRoute: pathname,
        formId: state.aiAgent?.activeFormId ?? null,
        eventType,
        sessionIntent: state.aiAgent?.sessionIntent ?? null,
        currentFormValues: state.aiAgent?.currentFormValues ?? null,
    };
};
